import React, { useState } from 'react';
import { Card, CardHeader, CardContent } from '../../components/ui';
import { Button, Input, Field, FieldLabel } from '../../components/ui';
import { PageHeader } from '@/components/layout/PageHeader';
import { adminBackofficeApi } from '../../services/adminBackoffice';
import toast from 'react-hot-toast';

const EMAIL_TYPES = [
    { value: 'booking_confirmation', label: 'Booking Confirmation' },
    { value: 'lesson_reminder', label: 'Lesson Reminder' },
    { value: 'payment_receipt', label: 'Payment Receipt' },
    { value: 'academy_announcement', label: 'Academy Announcement' },
    { value: 'contact_notification', label: 'Contact Notification' },
    { value: 'contact_confirmation', label: 'Contact Confirmation' },
    { value: 'trial_ending_reminder', label: 'Trial Ending Reminder' },
    { value: 'subscription_payment_receipt', label: 'Subscription Payment Receipt' },
    { value: 'student_limit_reached', label: 'Student Limit Reached' },
    { value: 'support_request_notification', label: 'Support Request Notification' },
    { value: 'support_request_confirmation', label: 'Support Request Confirmation' },
    { value: 'user_welcome', label: 'User Welcome (NEW)' },
    { value: 'student_welcome', label: 'Student Welcome (NEW)' },
    { value: 'test', label: 'Test Email' },
];

export default function EmailTesterPage() {
    const [emailType, setEmailType] = useState('user_welcome');
    const [recipient, setRecipient] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSendTest = async () => {
        if (!recipient.trim()) {
            toast.error('Recipient email is required');
            return;
        }

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(recipient.trim())) {
            toast.error('Please enter a valid email address');
            return;
        }

        setLoading(true);
        try {
            const res = await adminBackofficeApi.testEmail({
                emailType,
                recipient: recipient.trim(),
            });

            if (res.success) {
                toast.success(`Test email sent to ${recipient.trim()}`);
            } else {
                toast.error(res.error || 'Failed to send test email');
            }
        } catch (err: any) {
            toast.error(err?.response?.data?.error || err?.message || 'Failed to send test email');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <PageHeader
                title="Email Testing"
                help={<p>Test email templates by sending them to any email address with mock data.</p>}
            />

            <Card>
                <CardHeader>
                    <h2 className="text-lg font-medium text-gray-900 dark:text-white">Send Test Email</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        Test email templates by sending them to any email address with mock data
                    </p>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Email Type
                        </label>
                        <select
                            value={emailType}
                            onChange={(e) => setEmailType(e.target.value)}
                            className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 pl-3 pr-10 py-2 text-sm text-gray-900 dark:text-white"
                        >
                            {EMAIL_TYPES.map((type) => (
                                <option key={type.value} value={type.value}>
                                    {type.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <Field>
                        <FieldLabel htmlFor="email-tester-recipient">Recipient Email</FieldLabel>
                        <Input id="email-tester-recipient" type="email" value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder="test@example.com" />
                    </Field>

                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                        <p className="text-sm text-blue-800 dark:text-blue-200">
                            <strong>Note:</strong> Test emails will be sent with mock data. The selected email type will be
                            rendered with sample information for testing purposes.
                        </p>
                    </div>

                    <Button onClick={handleSendTest} disabled={loading}>
                        {loading ? 'Sending…' : 'Send Test Email'}
                    </Button>
                </CardContent>
            </Card>

            <Card className="mt-8">
                <CardHeader>
                    <h2 className="text-lg font-medium text-gray-900 dark:text-white">Available Email Types</h2>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Platform Emails</h3>
                            <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                                <li>• Contact Notification</li>
                                <li>• Contact Confirmation</li>
                                <li>• Trial Ending Reminder</li>
                                <li>• Subscription Payment Receipt</li>
                                <li>• Student Limit Reached</li>
                                <li>• Support Request Notification</li>
                                <li>• Support Request Confirmation</li>
                                <li className="text-green-600 dark:text-green-400 font-medium">• User Welcome (NEW)</li>
                            </ul>
                        </div>
                        <div>
                            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Academy Emails</h3>
                            <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                                <li>• Booking Confirmation</li>
                                <li>• Lesson Reminder</li>
                                <li>• Payment Receipt</li>
                                <li>• Academy Announcement</li>
                                <li className="text-green-600 dark:text-green-400 font-medium">• Student Welcome (NEW)</li>
                            </ul>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
